/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   ไฟล์ของโปรเจ็กต์ (หน้ารายละเอียดโปรเจ็กต์)
   - แบบบ้าน  : เก็บทุกเวอร์ชัน (อัปโหลดครั้งหนึ่ง = 1 เวอร์ชัน) ลูกค้าเห็นเสมอ
   - ไฟล์แนบ  : เอกสารอื่น ๆ เลือกได้ว่าลูกค้าเห็นไหม
============================================================ */
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Checkbox, Collapse, Flex, Form, Input, Modal, Popconfirm, Switch, Tag, Tooltip, Typography, message } from "antd";
import { DeleteOutlined, EyeInvisibleOutlined, EyeOutlined, UploadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import FileList from "../../../components/ak/FileList";
import FilePicker from "../../../components/ak/FilePicker";
import { ProjectFileService, apiError } from "../../../service/Ak.service";
import { openFile } from "../../../utils/fileOpen";
import { usePermission } from "../../../store/context/permission-context";

function UploadModal({ open, category, projectCode, onClose, onDone }) {
  const [form] = Form.useForm();
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const isPlan = category === "plan";

  useEffect(() => {
    if (open) {
      form.resetFields();
      setFiles([]);
    }
  }, [open]);

  const save = async () => {
    const v = await form.validateFields();
    if (!files.length) return message.warning("เลือกไฟล์ก่อน");
    setSaving(true);
    try {
      const r = await ProjectFileService.upload(
        { project_code: projectCode, category, title: v.title, remark: v.remark, customer_visible: isPlan ? "Y" : v.visible ? "Y" : "N" },
        files
      );
      message.success(isPlan ? `อัปโหลดแบบบ้าน เวอร์ชัน ${r.data.data.version_no} แล้ว` : "อัปโหลดแล้ว");
      onDone();
    } catch (e) {
      message.error(apiError(e, "อัปโหลดไม่สำเร็จ"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} title={isPlan ? "อัปโหลดแบบบ้าน (เวอร์ชันใหม่)" : "แนบไฟล์โปรเจ็กต์"} onCancel={onClose} onOk={save} okText="อัปโหลด" cancelText="ยกเลิก" confirmLoading={saving} destroyOnClose>
      {isPlan && <Alert type="info" showIcon className="mb-3" message="ไฟล์ที่อัปโหลดครั้งนี้รวมเป็น 1 เวอร์ชัน (เช่น แบบหลายแผ่น) และลูกค้าเห็นเสมอ" />}
      <Form form={form} layout="vertical" initialValues={{ visible: false }}>
        <Form.Item name="title" label={isPlan ? "ชื่อเวอร์ชัน" : "ชื่อเอกสาร"}>
          <Input maxLength={200} placeholder={isPlan ? "เช่น แบบก่อสร้าง / แก้ไขครั้งที่ 1 (ขยายห้องครัว)" : "เช่น สัญญาก่อสร้าง, ใบเสนอราคา"} />
        </Form.Item>
        <Form.Item name="remark" label="หมายเหตุ">
          <Input maxLength={500} />
        </Form.Item>
        {!isPlan && (
          <Form.Item name="visible" valuePropName="checked">
            <Checkbox>ให้ลูกค้าเห็นไฟล์นี้ใน /portal</Checkbox>
          </Form.Item>
        )}
      </Form>
      <FilePicker value={files} onChange={setFiles} />
    </Modal>
  );
}

export default function ProjectFiles({ projectCode, onCount }) {
  const { can } = usePermission();
  const canEdit = can("support_project", "edit");
  const canDel = can("support_project", "delete");
  const [rows, setRows] = useState([]);
  const [upload, setUpload] = useState(null); // 'plan' | 'file'

  const load = () =>
    ProjectFileService.list(projectCode)
      .then((r) => setRows(r.data.data || []))
      .catch((e) => message.error(apiError(e, "โหลดไฟล์ไม่สำเร็จ")));

  useEffect(() => {
    load();
  }, [projectCode]);

  const open = (f, inline) => openFile(() => ProjectFileService.blob(f.file_id, inline), f, inline).catch((e) => message.error(e.message));

  const versions = useMemo(() => {
    const map = new Map();
    rows.filter((r) => r.category === "plan").forEach((r) => {
      if (!map.has(r.version_no)) map.set(r.version_no, { version_no: r.version_no, title: r.title, created_date: r.created_date, uploaded_name: r.uploaded_name, remark: r.remark, files: [] });
      map.get(r.version_no).files.push(r);
    });
    return [...map.values()].sort((a, b) => b.version_no - a.version_no);
  }, [rows]);
  const others = rows.filter((r) => r.category === "file");

  useEffect(() => {
    onCount?.({ plan: versions.length, file: others.length });
  }, [versions.length, others.length]);

  const remove = (f) =>
    ProjectFileService.remove(f.file_id)
      .then(() => {
        message.success("ลบแล้ว");
        load();
      })
      .catch((e) => message.error(apiError(e)));

  const toggle = (f, on) =>
    ProjectFileService.patch({ file_id: f.file_id, customer_visible: on ? "Y" : "N" })
      .then(() => setRows((rs) => rs.map((r) => (r.file_id === f.file_id ? { ...r, customer_visible: on ? "Y" : "N" } : r))))
      .catch((e) => message.error(apiError(e)));

  const delBtn = (f) =>
    canDel && (
      <Popconfirm key="del" title="ลบไฟล์นี้?" onConfirm={() => remove(f)} okText="ลบ" okButtonProps={{ danger: true }} cancelText="ไม่">
        <Tooltip title="ลบ">
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Tooltip>
      </Popconfirm>
    );

  const versionHead = (v, latest) => (
    <Flex gap={8} align="center" wrap="wrap">
      <Typography.Text strong>เวอร์ชัน {v.version_no}</Typography.Text>
      {latest && <Tag color="green">ล่าสุด</Tag>}
      {v.title && <span>{v.title}</span>}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {dayjs(v.created_date).format("DD/MM/YYYY HH:mm")}
        {v.uploaded_name ? ` · ${v.uploaded_name}` : ""} · {v.files.length} ไฟล์
      </Typography.Text>
    </Flex>
  );

  return (
    <>
      <Card
        className="ak-card"
        title={
          <span>
            แบบบ้าน <Typography.Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>ลูกค้าเห็นทุกเวอร์ชัน</Typography.Text>
          </span>
        }
        extra={
          canEdit && (
            <Button type="primary" icon={<UploadOutlined />} onClick={() => setUpload("plan")}>
              อัปโหลดแบบบ้าน
            </Button>
          )
        }
      >
        {versions.length === 0 ? (
          <FileList files={[]} emptyText="ยังไม่มีแบบบ้าน — อัปโหลดแบบที่ตกลงกับลูกค้าไว้ที่นี่" />
        ) : (
          <>
            <div className="ak-plan-latest">
              {versionHead(versions[0], true)}
              {versions[0].remark && <Typography.Text type="secondary">{versions[0].remark}</Typography.Text>}
              <FileList files={versions[0].files} onOpen={open} showDate={false} showTitle={false} actions={(f) => [delBtn(f)]} />
            </div>
            {versions.length > 1 && (
              <Collapse
                ghost
                size="small"
                items={[
                  {
                    key: "old",
                    label: `เวอร์ชันก่อนหน้า (${versions.length - 1})`,
                    children: versions.slice(1).map((v) => (
                      <div key={v.version_no} className="ak-plan-old">
                        {versionHead(v, false)}
                        <FileList files={v.files} onOpen={open} showDate={false} showTitle={false} actions={(f) => [delBtn(f)]} />
                      </div>
                    )),
                  },
                ]}
              />
            )}
          </>
        )}
      </Card>

      <Card
        className="ak-card"
        title="ไฟล์แนบ"
        extra={
          canEdit && (
            <Button icon={<UploadOutlined />} onClick={() => setUpload("file")}>
              แนบไฟล์
            </Button>
          )
        }
      >
        <FileList
          files={others}
          onOpen={open}
          emptyText="ยังไม่มีไฟล์แนบ (สัญญา ใบเสนอราคา รูปหน้างาน ฯลฯ)"
          actions={(f) => [
            <Tooltip key="vis" title={f.customer_visible === "Y" ? "ลูกค้าเห็นไฟล์นี้" : "ลูกค้าไม่เห็นไฟล์นี้"}>
              <Switch
                size="small"
                disabled={!canEdit}
                checked={f.customer_visible === "Y"}
                checkedChildren={<EyeOutlined />}
                unCheckedChildren={<EyeInvisibleOutlined />}
                onChange={(on) => toggle(f, on)}
              />
            </Tooltip>,
            delBtn(f),
          ]}
        />
      </Card>

      <UploadModal
        open={!!upload}
        category={upload}
        projectCode={projectCode}
        onClose={() => setUpload(null)}
        onDone={() => {
          setUpload(null);
          load();
        }}
      />
    </>
  );
}
